import DLabGrid from "../../components/DLabGrid/DLabGrid";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BookingMetaByEquipment, DEFAULT_FILTER } from "../../constants";
import useChangeEndTimeModal from "../my-booking/ChangeBookingEndTime/hooks/useChangeEndTimeModal";
import {
  buildProjectsLabel,
  filterParams,
  get4WeekStartAndEndInISOString,
  getBookedUserDisplay,
  replaceOffsetWithOther,
  valueOrEmpty,
  formatDateTimeInTimezone,
} from "../../utils/helpers/text/index";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { getAllData, clearGetAllDataCache } from "../../utils/helpers/fetching";
import { GET_BOOKING_DATA_BY_SITE_LIGHT } from "../../gql/bookingapi";
import {
  deriveShortDescription,
  MAX_RICH_DESCRIPTION_LENGTH,
  RichDescriptionCellRenderer,
} from "../../components/shared/RichDescription";
import { compose, withApollo } from "react-apollo";
import { connect, useSelector } from "react-redux";
import { selectIsPredOrGredUser } from "../../selectors/user";
import { loadPendingReservations as loadPendingReservationsAction } from "../booking-calendars/redux/actions";
import BookingMenuAction from "./BookingMenuAction";
import BackdropPortal from "../../components/shared/BackdropPortal";
import moment from "moment";
import { BookingContext } from "../booking/context";
import { APPROVE_BOOKING } from "../../gql/bookingapi/mutations";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import CheckInButton from "../../components/shared/CheckInButton/CheckInButton";
import { EN_DASH } from "@digitallab/grid-common-frontend-components";
import { BookingTypeChip } from "../../components/shared/BookingTypeChip";
import { BookingHazardousChip } from "../../components/shared/BookingHazardousChip";
import useInstrumentCache from "../../utils/hooks/useInstrumentCache";
import { useSharedFilterState } from "./SharedFilterContext";
import SaveConfiguration from "../../components/EquipmentList/Components/SaveConfiguration";
import { onAgGridFilterApply, sanitizeFilterModelForGrid } from "../../components/EquipmentList/Service/filterService";
import { convertStrToObjFilters } from "../../components/EquipmentList/Service/equipmentService";
import { onAgGridColumnApply } from "../../components/EquipmentList/Service/columnService";
import { getBookingDisplayText } from "../../components/shared/helpFunctions";
import { TOOL_PANELS, filterKeyMapping } from "../../components/EquipmentList/Constants/constants";
import { useBooking } from "../../components/EquipmentList/Service/commonsService";
import { hazardousStatusGetter } from "../../utils/helpers/data";
import { hasCheckInState } from "../../components/shared/checkInVisibility";
import {
  createDefaultCsvExportParams,
  createDefaultExcelExportParams,
  createClipboardCellProcessor,
  getExportableColumnKeys,
} from "../../utils/helpers/exportValueMapper";
import BookingsPaginationActions from "../../components/shared/BookingsPaginationActions";
import ProjectRenderer from "../equipment-list/ProjectRender";
import { DateWithRecurrenceCellRenderer } from "../../utils/helpers/text/DateWithRecurrenceCellRenderer";
import ChangeBookingEndTimeModal from "../my-booking/ChangeBookingEndTime/ChangeBookingEndTimeModal";
import HazardousRejectDialog from "../hazardous-materials/HazardousRejectDialog";
import AssistanceAssignmentDialog from "../hazardous-materials/AssistanceAssignmentDialog";

const serializeFilterModel = (model) => {
  try {
    return JSON.stringify(model || {});
  } catch (_error) {
    return "{}";
  }
};

export const buildEquipmentRowsFromMap = (equipmentMap = {}, individualEntriesMap = {}) => {
  const entryInventoryId = (entry) => `${entry?.inventoryId || entry?.resource || entry?.equipment?.inventoryId || ""}`;

  const buildBaseRow = (eq, bookingEq, status) => ({
    ...eq,
    ...bookingEq,
    id: bookingEq?.id,
    approval: bookingEq?.approval,
    status,
    active: status === "ACTIVE" || status === "PENDING",
    project: bookingEq.project,
    redPandaProjects: bookingEq.redPandaProjects,
    description: bookingEq.description,
    richDescription: bookingEq.richDescription,
    checkIn: bookingEq.checkInRequired,
  });

  const equipmentRows = [];

  Object.values(equipmentMap).forEach((eq) => {
    (eq?.bookings || []).forEach((bookingEq) => {
      const status = (bookingEq?.approval?.status ?? (bookingEq?.active ? "ACTIVE" : "CANCELLED")) ?? eq?.status;
      const entriesKey = `${bookingEq?.id}_${eq?.inventoryId}`;
      const entriesFromMap = individualEntriesMap[entriesKey];
      const entriesFromBooking = Array.isArray(bookingEq?.bookingEntries)
        ? bookingEq.bookingEntries
        : bookingEq?.bookingEntries?.items || [];

      const entryRows = (entriesFromMap || entriesFromBooking || []).filter(
        (entry) => entryInventoryId(entry) === `${eq?.inventoryId}`,
      );

      const baseRow = buildBaseRow(eq, bookingEq, status);

      if (!entryRows.length) {
        equipmentRows.push({
          ...baseRow,
          uniqId: `${eq?.inventoryId}_${bookingEq?.id}`,
          dateFrom: bookingEq?.dateFrom,
          dateTo: bookingEq?.dateTo,
          type: bookingEq?.type,
        });
        return;
      }

      entryRows.forEach((entry, entryIndex) => {
        equipmentRows.push({
          ...baseRow,
          bookingEntryId: entry?.id,
          uniqId: `${eq?.inventoryId}_${bookingEq?.id}_${entry?.id || entryIndex}`,
          dateFrom: entry?.start || bookingEq?.dateFrom,
          dateTo: entry?.end || bookingEq?.dateTo,
          type: entry?.type || bookingEq?.type,
        });
      });
    });
  });

  return equipmentRows;
};

const EquipmentBookingList = ({
  client,
  resetAll,
  searchQuery,
  setSearchQuery,
  setSelectedDateRange,
  setDisableResetAll,
  defaultShowColumns = [],
  selectedDateRange,
  setDropType,
  setIsDropBooking,
  setIsDialogOpen,
  selectedEquipment,
  setSelectedEquipment,
  setOpenMultiDeleteDialog,
  setEnableResetAll,
  setRowEquipmentId,
  tempRowData,
  setTempRowData,
  loadPendingReservations,
  setPopupEnableStatus,
  showAdvanceFilter,
  savedFilter,
  signeeFilter,
  setDisplayPreset,
  displayPreset,
  userFilters,
  userDisplayColumnMaster,
  setUserFilters,
  setUserDisplayColumnMaster,
  setSavedFilter,
  myLabFilter,
  showSideBar,
  dataModel,
  localUserFilters,
  currentSideBarPanel,
  setCurrentSideBarPanel,
  setLastDisplayColumns,
  setLastFavFilter,
  shouldFetchData,
  innerTableFilters,
  setInnerTableFilters,
}) => {
  const { nextToken, data, fetching, nextData, refreshData, dispatchAction } = useContext(PaginationContext);
  const { dispatchAction: bookingDispatchAction } = useContext(BookingContext);
  const user = useSelector((store) => store.user);
  const [gridApi, setGridApi] = useState(null);
  const [openHazardousRejectDialog, setOpenHazardousRejectDialog] = useState(false);
  const [hazardousBookingToReject, setHazardousBookingToReject] = useState(null);
  const [openAssistanceDialog, setOpenAssistanceDialog] = useState(false);
  const [bookingForAssistance, setBookingForAssistance] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [status, setStatus] = useState(false);
  const rowStyle = { background: "var(--one-color-cobas-accent-gray-100)" };
  const gridRef = useRef();
  const localUserFiltersFallbackRef = useRef(userFilters);
  const safeLocalUserFilters = localUserFilters || localUserFiltersFallbackRef;
  const abortRef = useRef(null);
  const debouncedFetchRef = useRef(null);
  const lastDateKeyRef = useRef(null);
  const isInitialRenderRef = useRef(true);

  const { filterState, isFilterDataReady, loadFilterData, updateCurrentFilter } = useSharedFilterState();

  const {
    isChangeEndTimeOpen,
    bookingForEndTimeChange,
    handleOpenChangeEndTimeModal: changeEndTimeOpen,
    handleCloseChangeEndTimeModal,
    handleSaveEndTime,
  } = useChangeEndTimeModal({client});

  const handleApprove = useCallback(
    async (booking) => {
      if (booking?.assistanceDeclaration?.requiresAssistance) {
        setBookingForAssistance(booking);
        setIsEditMode(false);
        setOpenAssistanceDialog(true);
        return;
      }

      try {
        dispatchAction({
          type: "fetching",
          payload: true,
        });

        await client.mutate({
          mutation: APPROVE_BOOKING,
          variables: {
            bookingId: booking.id,
            approval: {
              status: "APPROVED",
              approvedBy: user.user,
              approvedByEmail: user.email,
            },
            createdBy: user.user,
            createdByEmail: user.email,
          },
        });
      } catch (error) {
        console.error("Approval error:", error);
        snackbarService.show({
          type: "error",
          message: "Failed to approve booking",
          duration: 3000,
        });
      }
    },
    [client, dispatchAction, user],
  );

  const handleReject = useCallback((booking) => {
    setHazardousBookingToReject(booking);
    setOpenHazardousRejectDialog(true);
  }, []);

  const filterDefToNameMap = useMemo(() => {
    const map = new Map();
    (userFilters || []).forEach((f) => {
      const key = JSON.stringify(f?.filterDefinition || {});
      map.set(key, f?.filterName || f?.chipName || "Unnamed");
    });

    if (myLabFilter?.filterDefinition) {
      map.set(JSON.stringify(myLabFilter.filterDefinition), myLabFilter.filterName || "My Lab");
    }

    return map; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilters, myLabFilter, filterState?.currentFilter]);

  useEffect(() => {
    // BUG-FIX: Do NOT apply filterDefinition as an ag-grid column filter.
    // The booking list data is already pre-filtered server-side via OpenSearch
    // (loadInstruments uses filterDefinition). Applying filterDefinition to ag-grid
    // causes an empty list because OpenSearch fields (e.g. `location` = locationKey string)
    // differ from the row data shape (e.g. `location` = {building, floor, room} object).
    // Instead, just clear the ag-grid column filters so stale filters don't bleed
    // between predefined filter changes.
    if (gridRef?.current?.api) {
      gridRef.current.api.setFilterModel({});
    }
  }, [filterState?.currentFilter]);

  const onApplyFilterFromChip = useCallback(
    (savedFilterModel) => {
      if (savedFilterModel === undefined) return;

      const filterObj =
        savedFilterModel && typeof savedFilterModel === "string"
          ? JSON.parse(savedFilterModel)
          : savedFilterModel || DEFAULT_FILTER;

      const filterDefinitionObj = Object.keys(filterObj)?.includes("filterName")
        ? convertStrToObjFilters(savedFilterModel)?.filterDefinition
        : filterObj;
      onAgGridFilterApply({
        gridRef,
        savedFilterModel: filterDefinitionObj,
        filterKeyMapping,
      });

      const key = JSON.stringify(filterDefinitionObj);
      const matchedName = filterDefToNameMap.get(key) || "Custom";
      if (setSavedFilter && matchedName !== "Custom") {
        setSavedFilter(matchedName);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [setCurrentSideBarPanel],
  );

  const onApplyColumnsFromChip = useCallback(
    (chipObj) => {
      const savedShowcolumns = chipObj?.displayDefinition?.showColumns;
      onApplyColumns(savedShowcolumns);
      if (setDisplayPreset) {
        setDisplayPreset({
          definition: chipObj.displayDefinition,
          label: chipObj.chipName,
          name: chipObj.id,
          ...chipObj,
        });
      } // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDisplayPreset],
  );

  const savedFilterChips = useMemo(() => {
    const base = (
      myLabFilter?.filterDefinition?.inventoryIds?.length > 0
        ? [myLabFilter, ...(userFilters || [])]
        : [...(userFilters || [])]
    ).map((f) => ({
      ...f,
      chipName: f.chipName || f.filterName,
      filterName: f.filterName,
      filterDefinition: f.filterDefinition,
      userID: f.userID || user?.id,
    }));

    return base; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilters, myLabFilter, filterState?.currentFilter, user?.id]);

  const savedColumnChips = useMemo(() => {
    return (userDisplayColumnMaster || []).map((d) => ({
      ...d,
      chipName: d.chipName || d.displayName,
      displayName: d.displayName,
      displayDefinition:
        typeof d.displayDefinition === "string" ? JSON.parse(d.displayDefinition) : d.displayDefinition,
      userID: d.userID || user?.id,
    }));
  }, [userDisplayColumnMaster, user?.id]);

  const instrumentCache = useInstrumentCache({
    client,
    user,
    masterDataFilters: filterState.masterDataFilters,
  });

  const locationGetter = (params) => {
    const data = params?.data;
    return data?.locationKey;
  };
  const statusGetter = (params) => {
    const data = params?.data;
    if (!data?.active) {
      return "Cancelled";
    }
    // Check if booking has hazardous materials and is pending approval
    const normalizedStatus = data?.approval?.status?.toString().toUpperCase();
    if (data?.hazardousDeclaration?.hasHazardousMaterials && normalizedStatus === "PENDING") {
      return "Pending";
    }
    return "Active";
  };
  const reservedForGetter = (params) => {
    const users = (params?.data?.bookedUsers ?? []).filter(Boolean);
    if (!users.length) {
      return "-";
    }
    const display = getBookedUserDisplay(users[0]);
    const result = display.length > 10 ? display.substring(0, 10) + "... " : display;
    const suffix = users.length - 1 > 0 ? ` +${users.length - 1}` : "";
    return result + suffix;
  };
  const reservedForFilterFormatterGetter = (params) => {
    const users = (params?.data?.bookedUsers ?? []).filter(Boolean);
    if (!users.length) {
      return "-";
    }
    return users.map(getBookedUserDisplay);
  };
  const projectGetter = (params) => {
    const row = params?.data || {};
    return buildProjectsLabel({
      rowRedPandaProjects: row.redPandaProjects,
      userRedPandaProjects: user?.redPandaProjects,
      projectId: row.project,
    });
  };
  const descriptionGetter = (params) => {
    const shortValue = params?.data?.description;
    if (typeof shortValue === "string" && shortValue.trim().length > 0) {
      return shortValue;
    }
    const richValue = params?.data?.richDescription;
    if (typeof richValue === "string" && richValue.trim().length > 0) {
      return deriveShortDescription(richValue);
    }
    return valueOrEmpty(shortValue, false, "-");
  };
  const richDescriptionGetter = (params) => {
    const richValue = params?.data?.richDescription;
    if (typeof richValue === "string" && richValue.trim().length > 0) {
      return deriveShortDescription(richValue, MAX_RICH_DESCRIPTION_LENGTH);
    }
    const shortValue = params?.data?.description;
    return valueOrEmpty(shortValue, false, "-");
  };

  const fetchData = useCallback(async () => {
    const dateKey = JSON.stringify(selectedDateRange);
    if (lastDateKeyRef.current === dateKey) {
      return;
    }
    lastDateKeyRef.current = dateKey;

    dispatchAction({ type: "fetching", payload: true });

    let dateFrom, dateTo;
    if (selectedDateRange[0] instanceof Date) {
      const dateFromString = moment(selectedDateRange[0]).format();
      const dateToString = moment(selectedDateRange[1]).format();
      const offset = moment.tz(user.timezone).format("Z");
      dateFrom = moment(replaceOffsetWithOther(dateFromString, offset)).utc().format();
      dateTo = moment(replaceOffsetWithOther(dateToString, offset, true))
        .utc()
        .format();
    } else {
      dateFrom = moment(selectedDateRange[0]).utc().format();
      dateTo = moment(selectedDateRange[1]).utc().format();
    }

    const bookingFilter = {
      and: [{ active: { eq: true } }, { and: [{ dateFrom: { le: dateTo } }, { dateTo: { ge: dateFrom } }] }],
    };

    if (signeeFilter?.userId && signeeFilter.userId !== "null" && signeeFilter.userId !== "") {
      bookingFilter.and.push({ reservedForUsers: { contains: signeeFilter.userId } });
    }

    const bookingsResult = await getAllData({
      client,
      query: GET_BOOKING_DATA_BY_SITE_LIGHT,
      dataPath: ["data", "getBookingDataBySite"],
      drillData: true,
      variables: {
        filter: JSON.stringify(bookingFilter),
        dateFrom: dateTo,
        site: user.site,
        limit: 1000,
        nextToken,
      },
      cache: true,
      cacheTTLSeconds: 60,
    });

    const bookings = bookingsResult.items;

    const individualEntriesMap = {};
    bookings.forEach(b => {
      (b.bookingEntries || []).forEach(entry => {
        const bookingId = entry.bookingId || b.id;
        const inventoryId = entry.inventoryId;
        if (bookingId && inventoryId) {
          const key = `${bookingId}_${inventoryId}`;
          if (!individualEntriesMap[key]) {
            individualEntriesMap[key] = [];
          }
          individualEntriesMap[key].push(entry);
        }
      });
    });

    bookings.forEach(b => {
      const seen = new Set();
      const bookedEquipments = [];
      (b.bookingEntries || []).forEach((entry) => {
        const inventoryId = entry?.inventoryId || entry?.resource || entry?.equipment?.inventoryId;
        if (inventoryId && !seen.has(inventoryId)) {
          seen.add(inventoryId);
          bookedEquipments.push(inventoryId);
        }
      });
      b.bookedEquipments = bookedEquipments;
    });

    const equipmentMap = {};
    bookings.forEach((b) => {
      (b.bookedEquipments || []).forEach((id) => {
        if (!equipmentMap[id]) {
          equipmentMap[id] = {
            inventoryId: id,
            bookings: [],
          };
        }
        equipmentMap[id].bookings.push(b);
      });
    });
    const ids = Object.keys(equipmentMap);
    if (ids.length) {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const filterDef = filterState?.currentFilter?.filterDefinition || {};
      let loadInstrumentsResponse = await instrumentCache.loadInstruments(user.site, filterDef, {
        signal: abortRef.current.signal,
      });

      if (!loadInstrumentsResponse) {
        loadInstrumentsResponse = await instrumentCache.loadInstruments(user.site, filterDef);
      }

      if (!loadInstrumentsResponse) {
        loadInstrumentsResponse = { items: [] };
      }
      const { items: instruments } = loadInstrumentsResponse;
      instruments.forEach((inst) => {
        const instrumentId = `${inst.inventoryId}`;
        equipmentMap[instrumentId] &&
          Object.assign(equipmentMap[instrumentId], {
            instrumentId,
            ...inst,
          });
      });
    }

    for (const key of Object.keys(equipmentMap)) {
      // remove filtered instruments
      if (!equipmentMap[key]?.instrumentId) {
        delete equipmentMap[key];
      }
    }

    const equipmentRows = buildEquipmentRowsFromMap(equipmentMap, individualEntriesMap);
    dispatchAction({
      type: "nextToken",
      payload: { token: null, data: equipmentRows },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, instrumentCache, selectedDateRange, user.site, user.timezone, filterState, signeeFilter]);

  useEffect(() => {
    if (!isFilterDataReady) {
      dispatchAction({
        type: "fetching",
        payload: true,
      });
      return;
    }
    if (debouncedFetchRef.current) clearTimeout(debouncedFetchRef.current);
    debouncedFetchRef.current = setTimeout(fetchData, 400); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, isFilterDataReady, nextData, refreshData]);

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    if (isFilterDataReady) {
      if (debouncedFetchRef.current) clearTimeout(debouncedFetchRef.current);
      lastDateKeyRef.current = null;
      clearGetAllDataCache();
      fetchData();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchData, nextData]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (debouncedFetchRef.current) clearTimeout(debouncedFetchRef.current);
    },
    [],
  );

  const {
    currentFilterChip,
    onApplyColumns,
    currentColumnsChip,
    dateFromFormatter,
    dateToFormatter,
    onSave,
    icons,
    onRowSelected,
    onChipDelete,
    editBooking,
  } = useBooking({
    filterState,
    displayPreset,
    defaultShowColumns,
    gridRef,
    onAgGridColumnApply,
    savedFilter,
    isFilterDataReady,
    updateCurrentFilter,
    loadFilterData,
    user,
    gridApi,
    searchQuery,
    client,
    localUserFilters: safeLocalUserFilters,
    setUserFilters,
    setLastFavFilter,
    setUserDisplayColumnMaster,
    setLastDisplayColumns,
    userFilters,
    userDisplayColumnMaster,
    setSelectedEquipment,
    selectedEquipment,
    setDisplayPreset,
    setSavedFilter,
    dispatchAction,
    bookingDispatchAction,
    loadPendingReservations,
  });

  const actionRender = (params) => {
    return (
      <BookingMenuAction
        params={params}
        handlers={{
          deleteEquipment,
          editBooking,
          setRowEquipmentId,
          setPopupEnableStatus,
          handleApprove,
          handleReject,
          changeEndTimeOpen,
        }}
      />
    );
  };

  const overrideCellRender = (key, defaultObj) => {
    switch (key) {
      case "type":
        defaultObj = {
          ...defaultObj,
          filter: false,
          cellRenderer: (item) =>
            item?.data?.type ? <BookingTypeChip key={item.data.id} item={item.data} /> : EN_DASH,
          filterValueGetter: (params) => {
            return getBookingDisplayText(params?.data);
          },
          valueGetter: (params) => {
            return getBookingDisplayText(params?.data);
          },
        };
        break;
      case "checkIn":
        defaultObj = {
          ...defaultObj,
          cellRenderer: (item) =>
            hasCheckInState(item?.data) ? <CheckInButton key={item.data.id} item={item.data} /> : null,
        };
        break;
      case "dateFrom":
        defaultObj = {
          ...defaultObj,
          checkboxSelection: true,
          showDisabledCheckboxes: true,
          filter: "agDateColumnFilter",
          filterParams,
          valueFormatter: dateFromFormatter,
          filterValueGetter: dateFromFormatter,
          cellRenderer: DateWithRecurrenceCellRenderer,
        };
        break;
      case "dateTo":
        defaultObj = {
          ...defaultObj,
          filter: "agDateColumnFilter",
          filterParams,
          valueFormatter: dateToFormatter,
          filterValueGetter: dateToFormatter,
        };
        break;
      case "active":
        defaultObj = {
          ...defaultObj,
          valueGetter: statusGetter,
          filterValueGetter: statusGetter,
        };
        break;
      case "location":
        defaultObj = {
          ...defaultObj,
          valueGetter: locationGetter,
          filterValueGetter: locationGetter,
        };
        break;
      case "approval":
        defaultObj = {
          ...defaultObj,
          filter: true,
          valueGetter: statusGetter,
          cellRenderer: (item) => <BookingHazardousChip key={item.data.id} item={item.data} />,
          filterValueGetter: statusGetter,
        };
        break;
      case "hazardous":
        defaultObj = {
          ...defaultObj,
          filter: true,
          cellRenderer: (item) => <BookingHazardousChip key={item.data.id} item={item.data} />,
          filterValueGetter: hazardousStatusGetter,
          valueGetter: hazardousStatusGetter,
        };
        break;

      case "bookingFor":
      case "reservedForUsers":
        defaultObj = {
          ...defaultObj,
          valueGetter: reservedForGetter,
          filterValueGetter: reservedForFilterFormatterGetter,
        };
        break;
      case "project":
        defaultObj = {
          ...defaultObj,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultToNothingSelected: true,
            buttons: ["reset", "apply"],
          },
          cellRenderer: ProjectRenderer,
          filterValueGetter: projectGetter,
          cellStyle: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        };
        break;
      case "shouldAssisted":
        defaultObj = {
          ...defaultObj,
          cellClass: "action-render",
          filter: "agSetColumnFilter",
          filterParams: {
            defaultToNothingSelected: true,
            showTooltips: true,
            buttons: ["reset"],
            values: (params) => params.success(["Yes", "No"]),
          },
          filterValueGetter: (params) => (params?.data?.shouldAssisted === "1" ? "Yes" : "No"),
        };
        break;
      case "description":
        defaultObj = {
          ...defaultObj,
          hide: true,
          suppressColumnsToolPanel: true,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultToNothingSelected: true,
            buttons: ["reset", "apply"],
          },
          valueGetter: descriptionGetter,
          filterValueGetter: descriptionGetter,
          cellRenderer: RichDescriptionCellRenderer,
        };
        break;
      case "richDescription":
        defaultObj = {
          ...defaultObj,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultToNothingSelected: true,
            buttons: ["reset", "apply"],
          },
          valueGetter: richDescriptionGetter,
          filterValueGetter: richDescriptionGetter,
          cellRenderer: RichDescriptionCellRenderer,
        };
        break;
      case "actionButton":
        defaultObj = {
          ...defaultObj,
          width: 0,
          maxWidth: 50,
          field: "actions",
          headerName: "",
          filter: false,
          pinned: "right",
          lockVisible: true,
          suppressColumnsToolPanel: true,
          suppressMenu: true,
          lockPosition: "right",
          cellClass: "action-render",
          cellRenderer: actionRender,
        };

        break;

      case "detailExpander":
        defaultObj = {
          ...defaultObj,
          cellRenderer: "agGroupCellRenderer",
          pinned: "left",
        };

        break;

      case "createdBy": {
        const createdByGetter = (params) => {
          const creatorId = params?.data?.createdBy;
          if (!creatorId) return "-";
          const bookedUsers = params?.data?.bookedUsers ?? [];
          const creator = bookedUsers.find((u) => (u.user || u.userId) === creatorId);
          if (creator) {
            return creator.name || `${creator.givenName ?? ""} ${creator.familyName ?? ""}`.trim() || creator.email;
          }
          return params?.data?.createdByEmail || creatorId;
        };
        defaultObj = {
          ...defaultObj,
          valueGetter: createdByGetter,
          filterValueGetter: createdByGetter,
        };
        break;
      }
      case "createdAt":
        defaultObj = {
          ...defaultObj,
          filter: "agDateColumnFilter",
          valueFormatter: (params) => formatDateTimeInTimezone(params?.data?.createdAt, user.timezone),
          filterValueGetter: (params) => formatDateTimeInTimezone(params?.data?.createdAt, user.timezone),
        };
        break;

      default:
        defaultObj = {
          ...defaultObj,
          valueGetter: (params) => valueOrEmpty(params?.data[key] ?? "-", false, "-"),
          filterValueGetter: (params) => valueOrEmpty(params?.data[key] ?? "-", false),
        };
        break;
    }
    return defaultObj;
  };

  const tableColumn = useMemo(() => {
    const isBookingAdmin = user?.roles?.some((role) => role === "BookingAdmin");
    const isPredOrGredUser = selectIsPredOrGredUser(user);
    // Equipment columns are keyed into the same map, so a later entry replaces an earlier one. That
    // is wanted for the shared keys (equipmentId, serialNumber, ...), where the schema supplies the
    // filter and renderer. It is not wanted for booking's own locked columns: `description` exists
    // on both sides and means different things - the booking's description here, the equipment's in
    // the schema. Booking keeps the keys it locks.
    const bookingOwned = new Set(
      Object.keys(BookingMetaByEquipment.fields).filter((key) => BookingMetaByEquipment.fields[key].lockVisible),
    );
    const dataModelFull = [
      ...Object.entries(BookingMetaByEquipment.fields)
        .filter(([, meta]) => !meta.adminOnly || isBookingAdmin)
        .filter(([, meta]) => !meta.siteRestricted || isPredOrGredUser)
        .map(([key, meta]) => ({ key, meta })),
      ...dataModel.filter((col) => !bookingOwned.has(col.key)).map((col) => ({ key: col.key, meta: col })),
    ];

    const baseMap = dataModelFull.reduce((acc, { key, meta }) => {
      acc[key] = overrideCellRender(key, {
        headerName: meta.val ?? meta.text,
        field: key,
        sortable: meta.sortable ?? true,
        floatingFilter: true,
        filter: meta.filter ?? true,
        filterParams: meta.filterParams,
        resizable: true,
        headerTooltip: meta.val ?? meta.text,
        width: meta.width || 200,
        maxWidth: meta.maxWidth || 200,
        minWidth: meta.minWidth || 200,
        ...meta,
      });

      return acc;
    }, {});

    return Object.values(baseMap); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BookingMetaByEquipment.fields, dataModel, showAdvanceFilter, user]);

  const onGridReady = useCallback((params) => {
    setGridApi(params);
  }, []);

  useEffect(() => {
    if (!gridApi?.api || !innerTableFilters) return;
    if (Object.keys(innerTableFilters).length === 0) return;
    const sanitizedFilters = sanitizeFilterModelForGrid(gridApi.api, innerTableFilters);
    const currentFilters = sanitizeFilterModelForGrid(gridApi.api, gridApi.api.getFilterModel() || {});
    if (serializeFilterModel(currentFilters) === serializeFilterModel(sanitizedFilters)) {
      return;
    }
    gridApi.api.setFilterModel(sanitizedFilters);
  }, [gridApi, innerTableFilters]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      resizable: true,
      suppressMenu: true,
      sortable: true,
    };
  }, []);

  useEffect(() => {
    if (gridApi && resetAll) {
      gridRef.current.api.setFilterModel();
      setInnerTableFilters && setInnerTableFilters({});
      setSearchQuery("");
      setSelectedEquipment([]);
      setSelectedDateRange([
        get4WeekStartAndEndInISOString("start", user.timezone),
        get4WeekStartAndEndInISOString("end", user.timezone),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetAll]);

  const isRowSelectable = useMemo(() => {
    return (params) => {
      const node = params.level;
      const isGroupRow = node === 0;
      const isActive =
        params.data.active === true &&
        moment(new Date(params?.data?.dateFrom)).tz(user.timezone).format("YYYY-MM-DD HH:mm") >
          moment(new Date()).tz(user.timezone).format("YYYY-MM-DD HH:mm");
      if (isGroupRow) {
        params.selectable = isActive;
      }
      return isGroupRow && isActive;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTempRowData(data);
    if (data.length > 0 && gridRef.current?.api) {
      gridRef.current.api.setRowData(data);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const scrollEvent = () => {
    const container = document.querySelector(".grid-container");
    if (Math.ceil(container.clientHeight + container.scrollTop) >= container.scrollHeight && nextToken) {
      dispatchAction({ type: "nextData" });
    }
  };

  const deleteEquipment = ({ type = "cancel" } = {}) => {
    setIsDialogOpen(true);
    setDropType(type);
    setIsDropBooking(true);
  };

  useEffect(() => {
    safeLocalUserFilters.current = userFilters; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilters]);

  const sideBarDef = useMemo(() => {
    const getOpenedToolPanelSafely = () => {
      try {
        return gridRef?.current?.api?.getOpenedToolPanel?.() || null;
      } catch (_error) {
        return null;
      }
    };

    const columnsDef = {
      minWidth: 200,
      width: 276,
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "column-icon",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressColumnSelectAll: true,
      },
    };

    const filtersDef = {
      minWidth: 200,
      width: 276,
      id: "filters",
      labelDefault: "Filters",
      labelKey: "filters",
      iconKey: "filter",
      toolPanel: "agFiltersToolPanel",
    };

    const saveDef = {
      minWidth: 200,
      width: 276,
      id: "save_configuration",
      labelDefault: "Saved configurations",
      labelKey: "loading",
      iconKey: "setting-icon",
      toolPanel: () => {
        const savedChips =
          currentSideBarPanel === TOOL_PANELS.filters
            ? savedFilterChips.map((f) => ({ ...f, chipName: f.chipName || f.filterName }))
            : savedColumnChips.map((c) => ({ ...c, chipName: c.chipName || c.displayName }));

        const savedChip =
          currentSideBarPanel === TOOL_PANELS.filters ? { ...currentFilterChip } : { ...currentColumnsChip };

        const sideBars = gridRef?.current?.api?.getSideBar();
        const panels = sideBars
          ? sideBars.toolPanels?.map((p) => p.id)
          : [TOOL_PANELS.filters, TOOL_PANELS.saveConfiguration];

        return (
          <SaveConfiguration
            storeUser={user}
            savedChips={savedChips}
            savedChip={savedChip}
            currentSideBarPanel={currentSideBarPanel}
            gridRef={gridRef}
            onApplyColumns={onApplyColumnsFromChip}
            onApplyFilter={onApplyFilterFromChip}
            defaultShowColumns={defaultShowColumns}
            defaultFilterObj={DEFAULT_FILTER}
            sideBarPanels={[...panels]}
            user={user}
            onSave={onSave}
            onChipDelete={onChipDelete}
          />
        );
      },
    };

    const toolPanels = currentSideBarPanel === TOOL_PANELS.columns ? [columnsDef, saveDef] : [filtersDef, saveDef];

    return {
      toolPanels,
      defaultToolPanel: gridRef?.current?.api?.isSideBarVisible()
        ? getOpenedToolPanelSafely() || currentSideBarPanel
        : currentSideBarPanel,
      hiddenByDefault: !showSideBar,
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showSideBar,
    currentSideBarPanel,
    gridRef,
    savedFilterChips,
    savedColumnChips,
    currentFilterChip,
    currentColumnsChip,
    displayPreset,
    user,
    filterState?.currentFilter,
    onApplyColumnsFromChip,
    onApplyFilterFromChip,
  ]);
  const attr = useMemo(
    () => ({
      height: "100%",
      columnDefs: tableColumn,
      animateRows: true,
      rowExport: false,
      includeHiddenColumnsInQuickFilter: true,
      sideBar: sideBarDef,
      allowContextMenuWithControlKey: true,
      getContextMenuItems: (params) => {
        return [
          "copy",
          "copyWithHeaders",
          "copyWithGroupHeaders",
          "separator",
          {
            name: "Export",
            subMenu: [
              {
                name: "Export Selected Rows",
                subMenu: [
                  {
                    name: "CSV Export",
                    action: () => {
                      const csvParams = {
                        ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                      };
                      params.api.exportDataAsCsv(csvParams);
                    },
                  },
                  {
                    name: "Excel Export",
                    action: () => {
                      const excelParams = {
                        ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                      };
                      params.api.exportDataAsExcel(excelParams);
                    },
                  },
                ],
              },
              "separator",
              {
                name: "Export All (Filtered)",
                subMenu: [
                  {
                    name: "CSV Export",
                    action: () => {
                      const csvParams = {
                        ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                        onlySelected: false,
                      };
                      params.api.exportDataAsCsv(csvParams);
                    },
                  },
                  {
                    name: "Excel Export",
                    action: () => {
                      const excelParams = {
                        ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                        columnKeys: getExportableColumnKeys(tableColumn),
                        onlySelected: false,
                      };
                      params.api.exportDataAsExcel(excelParams);
                    },
                  },
                ],
              },
            ],
          },
        ];
      },
      defaultCsvExportParams: createDefaultCsvExportParams({}, {}, user),
      defaultExcelExportParams: createDefaultExcelExportParams({}, {}, user),
      processCellForClipboard: createClipboardCellProcessor({}, user),
      defaultToolPanel: "filters",
      hiddenByDefault: true,
      rowData: tempRowData,
      icons: icons,
      onGridReady,
    }),
    [tableColumn, sideBarDef, tempRowData, icons, user, onGridReady],
  );

  const handleGridFilterChanged = useCallback(
    (params) => {
      const filters = sanitizeFilterModelForGrid(params.api, params.api.getFilterModel());
      if (setInnerTableFilters && serializeFilterModel(filters) !== serializeFilterModel(innerTableFilters)) {
        setInnerTableFilters(filters);
      }
      const hasFilters = Object.keys(filters).length > 0;

      if (params?.afterDataChange === false) {
        setEnableResetAll(hasFilters);
      }

      if (Object.keys(filters).length > 0) {
        setDisableResetAll(false);
      } else {
        setDisableResetAll(true);
      }
    },
    [innerTableFilters, setDisableResetAll, setEnableResetAll, setInnerTableFilters],
  );

  return (
    <>
      <BackdropPortal show={fetching} />

      <div
        className="grid-container"
        onScroll={scrollEvent}
        style={{
          width: "100%",
          height: "100%",
          minHeight: "415px",
        }}
      >
        <DLabGrid
          {...attr}
          pagination={false}
          suppressPaginationPanel={false}
          defaultColDef={defaultColDef}
          rowSelection={"multiple"}
          suppressRowClickSelection={true}
          autoGroupColumnDef={"autoGroupColumnDef"}
          onRowSelected={onRowSelected}
          isRowSelectable={isRowSelectable}
          gridRef={gridRef}
          rowStyle={rowStyle}
          onFilterChanged={handleGridFilterChanged}
        />
      </div>
      <BookingsPaginationActions
        selectedCount={selectedEquipment?.length}
        onCancelClick={() => setOpenMultiDeleteDialog(true)}
      />

      <ChangeBookingEndTimeModal
        isOpen={isChangeEndTimeOpen}
        onClose={handleCloseChangeEndTimeModal}
        booking={bookingForEndTimeChange}
        onSave={handleSaveEndTime}
        initialSelectedResourceId={bookingForEndTimeChange?.instrumentId}
      />

      {openHazardousRejectDialog && (
        <HazardousRejectDialog
          openRejectDialog={openHazardousRejectDialog}
          setOpenRejectDialog={setOpenHazardousRejectDialog}
          booking={hazardousBookingToReject}
          user={user}
          force={true}
          status={status}
          setStatus={setStatus}
        />
      )}

      {openAssistanceDialog && (
        <AssistanceAssignmentDialog
          open={openAssistanceDialog}
          onClose={() => {
            setOpenAssistanceDialog(false);
            setBookingForAssistance(null);
            setIsEditMode(false);
          }}
          booking={bookingForAssistance}
          client={client}
          onApprovalComplete={() => {
            setOpenAssistanceDialog(false);
            setBookingForAssistance(null);
            setIsEditMode(false);
            setTimeout(() => {
              dispatchAction({
                type: "refresh",
              });
            }, 500);
          }}
          user={user}
          setStatus={setStatus}
          isEditMode={isEditMode}
        />
      )}
    </>
  );
};

export default compose(
  connect(null, {
    loadPendingReservations: loadPendingReservationsAction,
  }),
  withApollo,
)(EquipmentBookingList);
